import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/pages/interface/components/ui/dialog";
import { Checkbox } from "@/pages/interface/components/ui/checkbox";
import { Skeleton } from "@/pages/interface/components/ui/skeleton";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/pages/interface/components/ui/collapsible";
import { Button } from "@/pages/interface/components/ui/button";
import { cn } from "@/lib/utils";
import { useSyncEngine } from "./useSyncEngine";
import { VENDOR_CONFIG, PUSHABLE_STATUSES } from "./vendorConfig";
import { SyncRow } from "./SyncRow";
import { BatchBar } from "./BatchBar";
import { ConflictReviewModal } from "./ConflictReviewModal";
import type { SyncScope, SyncStatus, SyncStoryRow } from "@/api/syncApi";

export interface SyncPanelProps {
    open: boolean;
    onClose: () => void;
    scope: SyncScope;
    currentStoryId?: string;
    /**
     * Optional callback to navigate to the integrations settings view (e.g. via
     * in-memory `setCurrentView` in pages/interface/App.tsx). When provided, the
     * "Reconnect" CTA closes this panel and invokes it instead of rendering a
     * full-page-reloading `<a href="/dashboard?view=integrations">` link.
     * Callers embedding SyncPanel (see Task 16) should wire this up.
     */
    onNavigateToIntegrations?: () => void;
}

const COUNT_META: Array<{ status: SyncStatus; label: string; dot: string }> = [
    { status: "SYNCED", label: "synced", dot: "bg-emerald-500" },
    { status: "LOCAL_CHANGED", label: "to push", dot: "bg-amber-500" },
    { status: "REMOTE_CHANGED", label: "to update", dot: "bg-amber-500" },
    { status: "CONFLICT", label: "conflict", dot: "bg-rose-500" },
    { status: "NOT_SYNCED", label: "unsynced", dot: "bg-gray-400" },
    { status: "NOT_READY", label: "not ready", dot: "bg-gray-300" },
    { status: "ORPHANED", label: "orphaned", dot: "bg-gray-400" },
];

function groupByFeature(rows: SyncStoryRow[]) {
    const map = new Map<string, { title: string; rows: SyncStoryRow[] }>();
    for (const row of rows) {
        const key = row.feature_id != null ? String(row.feature_id) : "unassigned";
        if (!map.has(key)) map.set(key, { title: row.feature_title ?? "Other stories", rows: [] });
        map.get(key)!.rows.push(row);
    }
    return [...map.values()];
}

function groupNeedsAttention(rows: SyncStoryRow[]): boolean {
    return rows.some(r => r.status === "REMOTE_CHANGED" || r.status === "CONFLICT" || !!r.last_error);
}

function checkState(rows: SyncStoryRow[], selected: Set<string>): boolean | "indeterminate" {
    const count = rows.filter(r => selected.has(r.story_id)).length;
    if (count === 0) return false;
    return count === rows.length ? true : "indeterminate";
}

export function SyncPanel({ open, onClose, scope, currentStoryId, onNavigateToIntegrations }: SyncPanelProps) {
    const engine = useSyncEngine(scope, open);
    const { loading, scopeStatus, error, selected, toggle, toggleAll, clearSelection,
            rowState, pushing, pushSummary, pushProgress, pushSelected, pushOne, pullOne, resolveOne } = engine;
    const [reviewRow, setReviewRow] = useState<SyncStoryRow | null>(null);
    const [dismissedError, setDismissedError] = useState<string | null>(null);
    const [showPullNotice, setShowPullNotice] = useState(false);
    const preselectedFor = useRef<string | null>(null);

    useEffect(() => {
        if (!open) { preselectedFor.current = null; return; }
        if (currentStoryId && preselectedFor.current !== currentStoryId) {
            preselectedFor.current = currentStoryId;
            toggle(currentStoryId);
        }
    }, [open, currentStoryId, toggle]);

    // Reset dismissed-error tracking and the pull notice each time the panel
    // opens fresh, and re-arm the error banner if a *new* error string comes in.
    useEffect(() => {
        if (!open) { setShowPullNotice(false); setDismissedError(null); }
    }, [open]);

    // pullOne / resolveOne("theirs") only dispatch this event on success, so
    // listening for it is a simple, panel-local way to know a pull actually
    // landed without threading a success signal through useSyncEngine.
    useEffect(() => {
        if (!open) return;
        const handler = () => setShowPullNotice(true);
        window.addEventListener("storyContentUpdated", handler);
        return () => window.removeEventListener("storyContentUpdated", handler);
    }, [open]);

    const vendorCfg = scopeStatus?.vendor ? VENDOR_CONFIG[scopeStatus.vendor] : null;
    const disconnected = !!scopeStatus && scopeStatus.linked && !scopeStatus.connected;
    const rows = scopeStatus?.stories ?? [];
    const scopeNoun = scope.type === "product" ? "product" : "feature";
    const groups = useMemo(() => (scope.type === "product" ? groupByFeature(rows) : null), [scope.type, rows]);
    const pushableIds = useMemo(() => rows.filter(r => PUSHABLE_STATUSES.has(r.status)).map(r => r.story_id), [rows]);
    const pushableIdSet = useMemo(() => new Set(pushableIds), [pushableIds]);
    const selectAllState = checkState(rows.filter(r => pushableIdSet.has(r.story_id)), selected);

    function toggleGroup(groupRows: SyncStoryRow[]) {
        // Only pushable rows are candidates (see PUSHABLE_STATUSES) —
        // matching toggleAll's pushable-only behavior.
        const pushableRows = groupRows.filter(r => pushableIdSet.has(r.story_id));
        const allSelected = pushableRows.length > 0 && pushableRows.every(r => selected.has(r.story_id));
        pushableRows.forEach(r => {
            if (allSelected === selected.has(r.story_id)) toggle(r.story_id);
        });
    }

    const renderRow = (row: SyncStoryRow) => (
        <SyncRow
            key={row.story_id}
            row={row}
            selected={selected.has(row.story_id)}
            highlighted={row.story_id === currentStoryId}
            uiState={rowState[row.story_id]}
            vendor={vendorCfg ?? VENDOR_CONFIG.JIRA}
            actionsDisabled={disconnected}
            onToggle={() => toggle(row.story_id)}
            onAction={() => pushOne(row.story_id)}
            onReview={() => setReviewRow(row)}
            onLinkOut={onClose}
        />
    );

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            {/* sm: prefix required: the base dialog class sm:max-w-lg wins over an
                unprefixed max-w at the sm breakpoint, leaving the panel 512px. */}
            <DialogContent className="sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                        {vendorCfg && <vendorCfg.Icon width={20} height={20} />}
                        <span>{vendorCfg ? `${vendorCfg.label} sync` : "Sync"}</span>
                        {scopeStatus && (
                            <span className="flex flex-wrap items-center gap-3 text-xs font-normal text-gray-500">
                                {COUNT_META.filter(m => (scopeStatus.counts[m.status] ?? 0) > 0).map(m => (
                                    <span key={m.status} className="flex items-center gap-1">
                                        <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
                                        {scopeStatus.counts[m.status]} {m.label}
                                    </span>
                                ))}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {error && error !== dismissedError && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                        <span className="min-w-0 flex items-start gap-2 break-words leading-5">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            {error}
                        </span>
                        <button
                            type="button"
                            aria-label="Dismiss error"
                            onClick={() => setDismissedError(error)}
                            className="shrink-0 rounded p-0.5 text-rose-600 hover:bg-rose-100 hover:text-rose-800"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                {showPullNotice && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                        <span>Story updated. If it's open in the editor, the pulled content loads in automatically.</span>
                        <button
                            type="button"
                            aria-label="Dismiss notice"
                            onClick={() => setShowPullNotice(false)}
                            className="shrink-0 rounded p-0.5 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="space-y-2">
                        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                    </div>
                )}

                {!loading && scopeStatus && !scopeStatus.linked && (
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                        Link this product to {vendorCfg ? vendorCfg.label : "a tracker"} first.
                    </div>
                )}

                {!loading && disconnected && (
                    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                        <span className="flex items-center gap-2 text-xs text-amber-800">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            {`Your ${vendorCfg ? vendorCfg.label : "tracker"} connection needs to be re-authorized. Showing last-known status; syncing is paused.`}
                        </span>
                        {onNavigateToIntegrations ? (
                            <Button
                                size="sm"
                                className="shrink-0 bg-indigo-600 text-white hover:bg-indigo-700"
                                onClick={() => { onClose(); onNavigateToIntegrations(); }}
                            >
                                {`Reconnect to ${vendorCfg ? vendorCfg.label : "the tracker"}`}
                            </Button>
                        ) : (
                            <Button asChild size="sm" className="shrink-0 bg-indigo-600 text-white hover:bg-indigo-700">
                                <a href="/dashboard?view=integrations">{`Reconnect to ${vendorCfg ? vendorCfg.label : "the tracker"}`}</a>
                            </Button>
                        )}
                    </div>
                )}

                {!loading && scopeStatus && scopeStatus.linked && (
                    <>
                        {scopeStatus.remote_check_failed && !disconnected && (
                            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                {`Couldn't check ${vendorCfg?.label ?? "the tracker"} for updates — showing locally known status.`}
                            </div>
                        )}

                        {rows.length === 0 ? (
                            <p className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                                {`No user stories in this ${scopeNoun} yet.`}
                            </p>
                        ) : (
                            <>
                                <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-1 pb-2">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            aria-label="Select all"
                                            checked={selectAllState}
                                            onCheckedChange={toggleAll}
                                            disabled={disconnected}
                                            className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                        />
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {`Stories in this ${scopeNoun}`}
                                        </span>
                                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">{rows.length}</span>
                                    </div>
                                    <span className="hidden text-xs text-gray-400 sm:inline">
                                        Tip: sync a single story from its row, or several at once below.
                                    </span>
                                </div>

                                {/* Plain overflow div, not Radix ScrollArea: its viewport wraps
                                    content in a display:table div that sizes to the untruncated
                                    text width, blowing the rows out past the dialog edge. */}
                                <div className="max-h-[420px] overflow-y-auto">
                                    {groups ? (
                                        groups.map(group => {
                                            const pushableGroupRows = group.rows.filter(r => pushableIdSet.has(r.story_id));
                                            const groupState = checkState(pushableGroupRows, selected);
                                            return (
                                                <Collapsible key={group.title} defaultOpen={groupNeedsAttention(group.rows)}>
                                                    <CollapsibleTrigger className="flex w-full items-center gap-2 border-y border-gray-200 bg-gray-100 px-3 py-2 text-left">
                                                        <Checkbox
                                                            aria-label={`Select all in ${group.title}`}
                                                            checked={groupState}
                                                            onCheckedChange={() => toggleGroup(group.rows)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={disconnected}
                                                            className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                                        />
                                                        <span className="flex-1 text-sm font-medium text-gray-700">{group.title}</span>
                                                        <span className="text-xs text-gray-400">{group.rows.length}</span>
                                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                                    </CollapsibleTrigger>
                                                    <CollapsibleContent>
                                                        {group.rows.map(renderRow)}
                                                    </CollapsibleContent>
                                                </Collapsible>
                                            );
                                        })
                                    ) : (
                                        rows.map(renderRow)
                                    )}
                                </div>

                                {!disconnected && (
                                    <BatchBar
                                        selectedCount={selected.size}
                                        total={rows.length}
                                        vendor={vendorCfg ?? VENDOR_CONFIG.JIRA}
                                        pushing={pushing}
                                        progress={pushProgress}
                                        summary={pushSummary}
                                        onClear={clearSelection}
                                        onPush={pushSelected}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {reviewRow && (
                <ConflictReviewModal
                    storyId={reviewRow.story_id}
                    storyTitle={reviewRow.title}
                    mode={reviewRow.status === "CONFLICT" ? "conflict" : "remote"}
                    vendor={vendorCfg ?? VENDOR_CONFIG.JIRA}
                    onClose={() => setReviewRow(null)}
                    onAccept={() => (reviewRow.status === "CONFLICT" ? resolveOne(reviewRow.story_id, "theirs") : pullOne(reviewRow.story_id))}
                    onKeepOurs={() => resolveOne(reviewRow.story_id, "ours")}
                />
            )}
        </Dialog>
    );
}
